import React, { useRef, useEffect, ReactNode } from "react";
import axios, { CancelTokenSource } from "axios";

import CustomAsyncSelect from "src/app/components/CustomSelect/CustomAsyncSelect";
import { showErrorToast } from "src/utils/methods";
import { TimeoutId } from "src/types";

interface SearchSelectDropdownProps<T> {
  value: T | T[] | null;
  fetchOptions: (
    inputValue: string,
    cancelToken: CancelTokenSource,
  ) => Promise<T[]>;
  placeholder: string;
  handleChange: ((newOptions: T[]) => void) | ((newOption: T) => void);
  isMulti?: boolean;
  withTiles?: boolean;
  id?: string;
  afterMenuList?: ReactNode;
  openMenuOnFocus?: boolean;
  isDisabled?: boolean;
  filterOption?: (option: T, input: string) => boolean;
}

export default function SearchSelectDropdown<T>({
  value,
  fetchOptions,
  placeholder,
  handleChange,
  isMulti,
  withTiles,
  id,
  afterMenuList,
  openMenuOnFocus,
  isDisabled,
  filterOption,
}: SearchSelectDropdownProps<T>) {
  const debounceTimeoutRef = useRef<TimeoutId | null>(null);
  const cancelToken = useRef<CancelTokenSource | null>(null);

  const abort = () => {
    cancelToken?.current?.cancel();
  };

  const handleLoadOptions = (inputValue: string): Promise<T[]> => {
    return new Promise((resolve) => {
      abort();
      cancelToken.current = axios.CancelToken.source();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const options = await fetchOptions(
            inputValue,
            cancelToken.current as CancelTokenSource,
          );
          resolve(options);
        } catch (error) {
          if (axios.isCancel(error)) {
            console.warn("Request canceled");
          } else {
            showErrorToast();
            console.error(error);
          }
          resolve([]);
        }
      }, 500);
    });
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      abort();
    };
  }, []);

  return (
    <CustomAsyncSelect
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      loadOptions={handleLoadOptions}
      isMulti={isMulti}
      withTiles={withTiles}
      id={id}
      afterMenuList={afterMenuList}
      openMenuOnFocus={openMenuOnFocus}
      isDisabled={isDisabled}
      filterOption={filterOption}
    />
  );
}
